const groupPrefix = createApiRoute(ApiModule.ORDER, ApiAuthorization.EMPLOYEE, ApiDataModel.CLIENT_DIET_DAYS)
const groupPrefixDailyDeliveries = createApiRoute(ApiModule.ORDER, ApiAuthorization.EMPLOYEE, ApiDataModel.BOXES)
const groupPrefixDailyDeliveriesCodes = createApiRoute(ApiModule.DIETARY, ApiDataModel.MEAL_CODES)
const groupPrefixDailyDeliveriesExport = createApiRoute(ApiModule.DIETARY, ApiDataModel.MATERIALS)
const groupPrefixBoxPackagings = createApiRoute(ApiModule.ORDER, ApiAuthorization.EMPLOYEE, ApiDataModel.BOX_PACKAGINGS)
const groupPrefixUnpaid = createApiRoute(ApiModule.ORDER, ApiAuthorization.EMPLOYEE)

interface FetchOrderDaysProps {
	clientID: number | undefined
	dayFrom: Date | undefined
	dayTo: Date | undefined
	status?: EDietStatus | undefined
	clientDietID?: number | undefined
	page?: number | undefined
	perPage?: number | undefined
	accumulatedData?: IOrderDay[] | undefined
}

export const apiOrderDays = {
	fetchOrderDays: async ({
		clientID,
		dayFrom,
		dayTo,
		status,
		clientDietID,
		page = 1,
		perPage = 50,
		accumulatedData = [],
	}: FetchOrderDaysProps): Promise<{ data: IOrderDayModel[]; meta: IApiMetaModel }> => {
		const dateFrom = dayFrom ? dateToFilterFormat(dayFrom) : undefined
		const dateTo = dayTo ? dateToFilterFormat(dayTo) : undefined

		const { data, meta } = await apiCommon<IOrderDay[]>(`${groupPrefix}`, {
			method: 'GET',
			...createQuery(
				{ page, perPage },
				{
					date_from: dateFrom,
					date_to: dateTo,
					'clientDiet.client_id': clientID,
					'clientDiet.status': status,
					client_diet_id: clientDietID,
				},
			),
		})

		if (!meta) {
			throw new Error('Meta information is missing from API response')
		}

		const newAccumulatedData = [...accumulatedData, ...data]

		const currentPage = meta.currentPage ?? 0
		const lastPage = meta.lastPage ?? 0

		if (currentPage < lastPage) {
			return apiOrderDays.fetchOrderDays({
				clientID,
				dayFrom,
				dayTo,
				status,
				clientDietID,
				perPage,
				page: page + 1,
				accumulatedData: newAccumulatedData,
			})
		}

		return { data: newAccumulatedData.map(OrderDay), meta }
	},

	fetchOrderDay: async (orderDayID: number) => {
		const { data } = await apiCommon<IOrderDay>(`${groupPrefix}/${orderDayID}`, { method: 'GET' })

		return OrderDay(data)
	},
	calculateOrderDayMealChange: async (orderDayID: number, mealSlots: { uuid: string; variant_id: number }[]) => {
		const { data } = await apiCommon<IOrderDayChange>(`${groupPrefix}/${orderDayID}/select-meals/calculate`, {
			method: 'POST',
			body: { client_meal_slots: mealSlots },
		})
		return OrderDayChange(data)
	},

	selectOrderDayMeals: async (clientDietDayUUID: string, mealSlots: { uuid: string; variant_id: number }[]) => {
		const { data } = await apiCommon<IOrderDay>(`${groupPrefix}/${clientDietDayUUID}/select-meals`, {
			method: 'POST',
			body: { client_meal_slots: mealSlots.map(s => ({ ...s, selection_source: ESelectionSource.CRM })) },
		})
		return { data: OrderDay(data) }
	},

	addOrderDayMeal: async (
		orderDayID: number,
		mealSlots: { uuid: string; variant_id: number }[],
		obj: IOrderDayChangeModel,
	) => {
		const { data } = await apiCommon<IOrderDay>(`${groupPrefix}/${orderDayID}/select-meals`, {
			method: 'POST',
			body: { ...orderDayChangeToJSON(obj), client_meal_slots: mealSlots },
		})
		return { data: OrderDay(data) }
	},

	deleteOrderDayMeal: async (clientDietDayUUID: string, payload: ICancelMealsPayloadModel) => {
		const { data } = await apiCommon<IOrderDay>(`${groupPrefix}/${clientDietDayUUID}/cancel-meals`, {
			method: 'POST',
			body: CancelMealsToPayload(payload),
		})
		return data
	},

	deleteOrderDayAddon: async (orderDayID: number, dayAddonIDs: number[]) => {
		const { data } = await apiCommon<IOrderDay>(`${groupPrefix}/${orderDayID}/cancel-meals`, {
			method: 'POST',
			body: { addon_client_diet_day_id: dayAddonIDs },
		})
		return { data: OrderDay(data) }
	},

	fetchCancelMealsCalculation: async (clientDietDayUUID: string, mealSlotUUIDs: string[]) => {
		const { data } = await apiCommon<IDietOperationCalculations>(
			`${groupPrefix}/${clientDietDayUUID}/cancel-meals/calculate`,
			{
				method: 'POST',
				body: { client_meal_slots: mealSlotUUIDs },
			},
		)
		return DietOperationCalculations(data)
	},

	generateDailyDeliveries: async (dates: Date[]) => {
		const formattedDates = dates.map(d => dateToFilterFormat(d))
		const fileName =
			formattedDates.length === 1
				? `${formattedDates[0]}-delivery-list.xlsx`
				: `${formattedDates[0]}-${formattedDates.at(-1)}-delivery-list.xlsx`

		const response = await apiCommonRaw<BlobPart>(`${groupPrefixDailyDeliveries}/download`, {
			method: 'POST',
			body: { dates: formattedDates },
			responseType: 'blob',
		})

		downloadFileFromResponse(response, fileName)
	},

	generateDailyDeliveriesCsv: async (dates: Date[]) => {
		const formattedDates = dates.map(d => dateToFilterFormat(d))

		const response = await apiCommonRaw<BlobPart>(`${groupPrefixDailyDeliveries}/download-csv`, {
			method: 'POST',
			body: { dates: formattedDates },
			responseType: 'blob',
		})

		downloadFileFromResponse(response, 'boxes.csv')
	},

	exportCommoditiesRaport: async (dateFrom: Date, dateTo: Date) => {
		const dateFromFormatted = dateToFilterFormat(dateFrom)
		const dateToFormatted = dateToFilterFormat(dateTo)

		const response = await apiCommonRaw<BlobPart>(
			`${groupPrefixDailyDeliveriesExport}/export/monoproducts/download?date_from=${dateFromFormatted}&date_to=${dateToFormatted}`,
			{
				method: 'GET',
				responseType: 'blob',
			},
		)

		downloadFileFromResponse(response, `${dateFromFormatted}-${dateToFormatted}-commodities-export.xlsx`)
	},

	fetchDailyDeliveriesCodes: async (date: Date) => {
		const response = await apiCommonRaw<BlobPart>(`${groupPrefixDailyDeliveriesCodes}/download`, {
			method: 'GET',
			...createQuery(undefined, undefined, undefined, { date: dateToFilterFormat(date) }),
			responseType: 'blob',
		})

		downloadFileFromResponse(response, `${dateToFilterFormat(date)}-packages-list.csv`)
	},

	exportBoxPackagingEmails: async (boxPackagingIds: number[]) => {
		const response = await apiCommonRaw<BlobPart>(`${groupPrefixBoxPackagings}/export-emails`, {
			method: 'GET',
			...createQuery(undefined, undefined, undefined, { box_packaging_ids: boxPackagingIds }),
			responseType: 'blob',
		})

		downloadFileFromResponse(
			response,
			'box-packagings-emails.xlsx',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		)
	},
	fetchCartMealSlots: async (parentClientDietUuid: string, date: Date) => {
		const { data } = await apiCommon<IOrderDayMealSlot[]>(`${groupPrefixUnpaid}/cart-meal-slots`, {
			method: 'GET',
			...createQuery(undefined, undefined, undefined, {
				parent_client_diet_uuid: parentClientDietUuid,
				date: dateToFilterFormat(date),
			}),
		})
		return (data ?? []).map(OrderDayMealSlot)
	},
}
